import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Star, Quote, CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { apiRequest } from '../lib/utils';

/**
 * Hero band displayed right below the homepage hero.
 * Auto-rotates the latest 5★ site reviews to maximize social proof above the fold.
 */
export default function ReviewsHeroBand() {
    const [reviews, setReviews] = useState([]);
    const [index, setIndex] = useState(0);

    useEffect(() => {
        apiRequest('/site-reviews/featured?limit=6')
            .then((d) => {
                const top = (d.reviews || []).filter((r) => r.rating === 5).slice(0, 5);
                setReviews(top);
            })
            .catch(() => setReviews([]));
    }, []);

    useEffect(() => {
        if (reviews.length <= 1) return;
        const id = setInterval(() => {
            setIndex((i) => (i + 1) % reviews.length);
        }, 6000);
        return () => clearInterval(id);
    }, [reviews.length]);

    if (reviews.length === 0) return null;

    const current = reviews[index];
    const prev = () => setIndex((i) => (i - 1 + reviews.length) % reviews.length);
    const next = () => setIndex((i) => (i + 1) % reviews.length);

    return (
        <section
            className="relative py-10 bg-gradient-to-r from-amber-50 via-white to-amber-50 dark:from-amber-950/20 dark:via-background dark:to-amber-950/20 border-y border-amber-200/50 dark:border-amber-800/30"
            data-testid="reviews-hero-band"
        >
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-center gap-2 mb-4">
                    <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map((n) => (
                            <Star key={n} className="h-4 w-4 fill-amber-400 text-amber-400" />
                        ))}
                    </div>
                    <span className="text-xs sm:text-sm font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wide">
                        Avis vérifiés clients
                    </span>
                </div>

                <div className="relative">
                    {reviews.length > 1 && (
                        <button
                            type="button"
                            onClick={prev}
                            className="hidden sm:flex absolute left-0 top-1/2 -translate-y-1/2 h-9 w-9 items-center justify-center rounded-full bg-white/80 dark:bg-card border border-amber-200 dark:border-amber-800 text-amber-700 hover:bg-white hover:shadow-md transition"
                            aria-label="Avis précédent"
                            data-testid="reviews-hero-prev"
                        >
                            <ChevronLeft className="h-5 w-5" />
                        </button>
                    )}

                    <div className="px-2 sm:px-12">
                        <Link to="/avis" className="block">
                            <blockquote className="text-center" key={current.review_id} data-testid={`reviews-hero-quote-${current.review_id}`}>
                                <Quote className="h-7 w-7 text-amber-500/50 mx-auto mb-3" />
                                <p className="text-base sm:text-lg lg:text-xl font-medium text-foreground italic max-w-3xl mx-auto leading-relaxed line-clamp-3">
                                    « {current.comment} »
                                </p>
                                <div className="mt-4 flex items-center justify-center gap-2 text-sm">
                                    <div className="h-8 w-8 rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-300 flex items-center justify-center text-xs font-bold">
                                        {(current.author_name || 'C')[0].toUpperCase()}
                                    </div>
                                    <span className="font-semibold text-foreground">{current.author_name}</span>
                                    {current.city && (
                                        <span className="text-muted-foreground">— {current.city}</span>
                                    )}
                                    {current.verified && (
                                        <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 text-xs ml-1">
                                            <CheckCircle className="h-3.5 w-3.5" /> Achat vérifié
                                        </span>
                                    )}
                                </div>
                            </blockquote>
                        </Link>
                    </div>

                    {reviews.length > 1 && (
                        <button
                            type="button"
                            onClick={next}
                            className="hidden sm:flex absolute right-0 top-1/2 -translate-y-1/2 h-9 w-9 items-center justify-center rounded-full bg-white/80 dark:bg-card border border-amber-200 dark:border-amber-800 text-amber-700 hover:bg-white hover:shadow-md transition"
                            aria-label="Avis suivant"
                            data-testid="reviews-hero-next"
                        >
                            <ChevronRight className="h-5 w-5" />
                        </button>
                    )}
                </div>

                {reviews.length > 1 && (
                    <div className="flex justify-center gap-1.5 mt-5" data-testid="reviews-hero-dots">
                        {reviews.map((_, i) => (
                            <button
                                key={i}
                                type="button"
                                onClick={() => setIndex(i)}
                                className={`h-1.5 rounded-full transition-all ${i === index ? 'w-6 bg-amber-500' : 'w-1.5 bg-amber-300/60 hover:bg-amber-400'}`}
                                aria-label={`Avis ${i + 1}`}
                            />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}
